import { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, Text } from "@livechat/design-system-react-components";

import { EventPlace, trackEngagement } from "helpers/analytics";
import { getPlainTextMessageText, messageIsEmpty, messageIsPlainText, parseEventMessageToHtml } from "helpers/tickets";
import { IEvent } from "interfaces/entities/ticket";
import { TicketsViewActions } from "store/views/tickets/actions";
import { getIsLoadingUnstrippedMessage, selectEventById } from "store/views/tickets/selectors";

import { MessageElementWrapper } from "./styles";

interface IProps {
  id: string;
  hasAttachments: boolean;
  showInitial: () => void;
}

export const MessageUnstrippedContent: FC<IProps> = ({ id, hasAttachments, showInitial }) => {
  const [uncutContent, setUncutContent] = useState<IEvent["uncutContent"] | null>(null);
  const isLoading = useSelector(getIsLoadingUnstrippedMessage);
  const dispatch = useDispatch();
  const event = useSelector(state => selectEventById(state, id));

  const onHideCutContent = (): void => {
    showInitial();
    trackEngagement("Tickets: Hide cut content clicked", EventPlace.TicketDetails);
  };

  useEffect(() => {
    if (!event?.uncutContent && !uncutContent) {
      dispatch(TicketsViewActions.fetchUnstrippedMessage({ eventId: id }));
    }
    if (event?.uncutContent && !uncutContent) {
      // Saving the uncut content in the state to avoid fetching it again when the event is updated.
      setUncutContent(event.uncutContent);
    }
  }, [event?.uncutContent, id, dispatch]);

  if (isLoading) {
    return <Text>Loading unstripped content…</Text>;
  }
  const messageHtml = uncutContent?.html;
  const messageText = uncutContent?.text;

  const message = (
    messageHtml && !messageIsEmpty(messageHtml) ? <>{messageHtml}</> : parseEventMessageToHtml(messageText)
  ) as ReactElement;

  const isPlainText = messageIsPlainText(message);
  const messageEmpty = messageIsEmpty(message);
  const messageOrAttachmentExists = !messageEmpty || hasAttachments;

  if (!messageOrAttachmentExists) {
    return <Text>This message has no content.</Text>;
  }

  return (
    <>
      {isPlainText && !messageEmpty && (
        <div
          dangerouslySetInnerHTML={{
            __html: getPlainTextMessageText(message),
          }}
          dir="auto"
        />
      )}
      {!isPlainText && <MessageElementWrapper>{message}</MessageElementWrapper>}
      <Button onClick={onHideCutContent} kind="link" size="compact">
        Hide cut content
      </Button>
    </>
  );
};
